import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { getProjectVitalFinanceOverview } from '../services/projectVitalService';
import {
  cancelPVReserve,
  createPVReserve,
  listPVReserves,
  payPVReserve,
  updatePVReserve,
} from '../services/projectVitalReserveService';
import type { PVReserve, PVReserveType, PVReserveWrite } from '../types/projectVitalReserves';

const CYAN = '#4DD0E1';
const AMBER = '#fcd34d';
const GREEN = '#4ade80';

const categoryNames: Record<PVReserveType, string> = {
  impuestos: 'Impuestos',
  cuota: 'Cuotas y Seguridad Social',
  operativa: 'Pagos operativos',
  otros: 'Otros compromisos',
};

function localToday(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
    .toISOString().slice(0, 10);
}

function euro(value: number | string): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function normalizeAmount(value: string): string | null {
  const candidate = value.trim().replace(',', '.');
  if (!/^\d+(?:\.\d{1,2})?$/.test(candidate)) return null;
  const n = Number(candidate);
  if (!Number.isFinite(n) || n <= 0 || n > 999999999999.99) return null;
  return n.toFixed(2);
}

function errorText(error: unknown): string {
  const candidate = error as { response?: { data?: { detail?: unknown } } };
  if (typeof candidate?.response?.data?.detail === 'string') {
    return candidate.response.data.detail;
  }
  return 'No se ha podido completar la operación. Inténtalo de nuevo.';
}

function freshForm(): PVReserveWrite {
  return {
    name: '',
    reserve_type: 'impuestos',
    amount: '',
    due_date: localToday(),
    account_id: '',
    description: null,
  };
}

/**
 * Reservas manuales: retienen disponibilidad estimada, pero NO afectan al
 * saldo bancario hasta que el usuario registra un pago real.
 */
export default function ProjectVitalProfessionalReservesSection() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [showHistory, setShowHistory] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PVReserve | null>(null);
  const [form, setForm] = useState<PVReserveWrite>(freshForm);
  const [formError, setFormError] = useState('');
  const [paying, setPaying] = useState<PVReserve | null>(null);
  const [paymentDate, setPaymentDate] = useState(localToday());
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [paymentError, setPaymentError] = useState('');
  const [cancelling, setCancelling] = useState<PVReserve | null>(null);

  const month = localToday().slice(0, 7);
  const reservesQuery = useQuery({
    queryKey: ['project-vital', 'professional-reserves'],
    queryFn: listPVReserves,
  });
  const accountQuery = useQuery({
    queryKey: ['project-vital', 'finance', month, 'global'],
    queryFn: () => getProjectVitalFinanceOverview(month, 'global'),
  });
  const reserves = reservesQuery.data ?? [];
  const accounts = accountQuery.data?.accounts ?? [];
  const activeAccounts = accounts.filter((account) => account.is_active);
  const accountsById = useMemo(
    () => new Map(accounts.map((account) => [account.id, account])),
    [accounts],
  );

  const pending = useMemo(
    () => reserves.filter((reserve) => reserve.status === 'pending')
      .sort((a, b) => a.due_date.localeCompare(b.due_date)),
    [reserves],
  );
  const historical = useMemo(
    () => reserves.filter((reserve) => reserve.status !== 'pending'),
    [reserves],
  );
  const pendingTotal = pending.reduce((total, reserve) => total + Number(reserve.amount), 0);
  const byAccount = activeAccounts.map((account) => {
    const reserved = pending
      .filter((reserve) => reserve.account_id === account.id)
      .reduce((total, reserve) => total + Number(reserve.amount), 0);
    return { ...account, reserved, available: Number(account.current_balance) - reserved };
  });
  const liquidity = byAccount.reduce((sum, account) => sum + Number(account.current_balance), 0);
  const available = liquidity - pendingTotal;
  const late = pending.filter((reserve) => reserve.due_date < localToday());

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['project-vital', 'professional-reserves'] }),
      queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance'] }),
      queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance-history'] }),
      queryClient.invalidateQueries({ queryKey: ['project-vital', 'finance-forecast'] }),
      queryClient.invalidateQueries({ queryKey: ['project-vital', 'cashflow'] }),
    ]);
  }

  const save = useMutation({
    mutationFn: ({ payload, id }: { payload: PVReserveWrite; id?: string }) =>
      id ? updatePVReserve(id, payload) : createPVReserve(payload),
    onSuccess: async () => {
      setFormOpen(false);
      setEditing(null);
      setFormError('');
      await refresh();
      enqueueSnackbar('Reserva guardada.', { variant: 'success' });
    },
    onError: (error) => setFormError(errorText(error)),
  });

  const payment = useMutation({
    mutationFn: () => {
      if (!paying) throw new Error('Selecciona la reserva.');
      return payPVReserve(paying.id, {
        payment_date: paymentDate,
        request_id: requestId,
      });
    },
    onSuccess: async () => {
      setPaying(null);
      setPaymentError('');
      setRequestId(crypto.randomUUID());
      await refresh();
      enqueueSnackbar('Pago registrado en la cuenta y reserva liquidada.', { variant: 'success' });
    },
    onError: (error) => setPaymentError(errorText(error)),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => cancelPVReserve(id),
    onSuccess: async () => {
      setCancelling(null);
      await refresh();
      enqueueSnackbar('Reserva cancelada. No se creó ningún movimiento.', { variant: 'success' });
    },
    onError: (error) => {
      setCancelling(null);
      enqueueSnackbar(errorText(error), { variant: 'error' });
    },
  });

  function openNew() {
    setEditing(null);
    setForm({ ...freshForm(), account_id: activeAccounts.find(
      (account) => account.financial_scope === 'professional',
    )?.id ?? activeAccounts[0]?.id ?? '' });
    setFormError('');
    setFormOpen(true);
  }

  function openEdit(reserve: PVReserve) {
    setEditing(reserve);
    setForm({
      name: reserve.name,
      reserve_type: reserve.reserve_type,
      amount: reserve.amount,
      due_date: reserve.due_date,
      account_id: reserve.account_id,
      description: reserve.description,
    });
    setFormError('');
    setFormOpen(true);
  }

  function saveForm() {
    const amount = normalizeAmount(form.amount);
    if (!amount || !form.name.trim() || !form.due_date || !form.account_id) {
      setFormError('Completa concepto, importe, fecha y cuenta correctamente.');
      return;
    }
    if (!activeAccounts.some((account) => account.id === form.account_id)) {
      setFormError('Selecciona una cuenta activa.');
      return;
    }
    save.mutate({
      id: editing?.id,
      payload: {
        ...form,
        name: form.name.trim(),
        amount,
        description: form.description?.trim() || null,
      },
    });
  }

  function openPayment(reserve: PVReserve) {
    setPaying(reserve);
    setPaymentDate(localToday());
    setRequestId(crypto.randomUUID());
    setPaymentError('');
  }

  return (
    <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${alpha('#94a3b8', .16)}` }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2}
        alignItems={{ md: 'center' }} justifyContent="space-between">
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <SavingsRoundedIcon sx={{ color: CYAN }} fontSize="small" />
            <Typography sx={{ color: '#f8fafc', fontWeight: 800, fontSize: '1.08rem' }}>
              Obligaciones y reservas
            </Typography>
          </Stack>
          <Typography sx={{ color: '#94a3b8', fontSize: '.82rem', mt: .4 }}>
            Aparta dinero para impuestos, cuotas u otros compromisos. Introduces los importes; no se calculan impuestos automáticamente.
          </Typography>
        </Box>
        <Button size="small" startIcon={<AddRoundedIcon />} onClick={openNew}
          disabled={accountQuery.isPending || activeAccounts.length === 0}
          sx={{ color: '#081624', bgcolor: CYAN, '&:hover': { bgcolor: '#88ffff' }, whiteSpace: 'nowrap' }}>
          Nueva reserva
        </Button>
      </Stack>

      {reservesQuery.isError && (
        <Alert severity="error" sx={{ mt: 1.6 }} action={
          <Button onClick={() => void reservesQuery.refetch()}>Reintentar</Button>
        }>No se pudieron cargar las reservas. Revisa que la migración esté aplicada.</Alert>
      )}
      {accountQuery.isError && (
        <Alert severity="error" sx={{ mt: 1.6 }} action={
          <Button onClick={() => void accountQuery.refetch()}>Reintentar</Button>
        }>No se pudieron consultar las cuentas. No se mostrará una disponibilidad estimada sin datos reales.</Alert>
      )}
      {(reservesQuery.isPending || accountQuery.isPending) && <CircularProgress size={22} sx={{ mt: 2 }} />}

      {!reservesQuery.isPending && !accountQuery.isPending && !reservesQuery.isError && !accountQuery.isError && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1, mt: 2 }}>
            {[
              { label: 'Liquidez global', amount: liquidity, color: '#f8fafc' },
              { label: 'Reservado pendiente', amount: pendingTotal, color: AMBER },
              { label: 'Disponible tras reservas', amount: available, color: available < 0 ? '#fca5a5' : GREEN },
            ].map((item) => (
              <Box key={item.label} sx={{ minWidth: 0, p: 1.4,
                border: `1px solid ${alpha('#94a3b8', .15)}`,
                borderRadius: 2, bgcolor: '#172338' }}>
                <Typography sx={{ color: '#94a3b8', fontSize: '.76rem' }}>{item.label}</Typography>
                <Typography sx={{ color: item.color, fontWeight: 850, fontSize: { xs: '1.2rem', md: '1.48rem' } }}>
                  {euro(item.amount)}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ color: '#8391a8', fontSize: '.74rem', mt: 1.1 }}>
            Liquidez global incluye tus cuentas personales y profesionales; las reservas solo reducen la disponibilidad estimada.
            No son gastos reales hasta registrar su pago y no se suman a previsiones recurrentes.
          </Typography>
          {available < 0 && (
            <Alert severity="warning" sx={{ mt: 1.2 }}>
              Has reservado más dinero del que hay en las cuentas actuales. Es un déficit previsto, no un descubierto bancario.
            </Alert>
          )}
          {late.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1.2 }}>
              Tienes {late.length} reserva(s) con fecha vencida pendientes de resolver.
            </Alert>
          )}
          {activeAccounts.length === 0 && (
            <Alert severity="info" sx={{ mt: 1.2 }}>
              Crea primero una cuenta activa, personal o profesional, para poder asignar fondos.
            </Alert>
          )}

          {byAccount.length > 0 && pending.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 1, mt: 1.4 }}>
              {byAccount.filter((account) => account.reserved > 0).map((account) => (
                <Box key={account.id} sx={{ p: 1.2, bgcolor: '#18263b', borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography sx={{ color: '#cbd5e1', fontSize: '.84rem', fontWeight: 750 }}>
                      {account.name} · {account.financial_scope === 'personal' ? 'Personal' : 'Profesional'}
                    </Typography>
                    <Typography sx={{ color: AMBER, fontSize: '.82rem' }}>Reservado: {euro(account.reserved)}</Typography>
                  </Stack>
                  <Typography sx={{ color: account.available < 0 ? '#fca5a5' : '#e2e8f0',
                    fontSize: '.82rem', mt: .4 }}>
                    Disponible estimado: {euro(account.available)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2} mb={1}>
            <Typography sx={{ fontWeight: 800 }}>Reservas pendientes ({pending.length})</Typography>
            {historical.length > 0 && (
              <Button size="small" onClick={() => setShowHistory((value) => !value)}>
                {showHistory ? 'Ocultar historial' : `Historial (${historical.length})`}
              </Button>
            )}
          </Stack>
          {pending.length === 0 && (
            <Typography sx={{ color: '#94a3b8', fontSize: '.83rem' }}>
              Sin reservas pendientes. Puedes crear una cuando conozcas el importe y el vencimiento.
            </Typography>
          )}
          <Stack spacing={.9}>
            {pending.map((reserve) => (
              <Box key={reserve.id} sx={{ p: 1.35, bgcolor: '#19273b', borderRadius: 2,
                border: `1px solid ${alpha('#94a3b8', .12)}` }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }}
                  justifyContent="space-between" spacing={1}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={.6} alignItems="center">
                      <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>{reserve.name}</Typography>
                      <Chip label={categoryNames[reserve.reserve_type]} size="small"
                        sx={{ height: 21, color: '#cbd5e1', bgcolor: '#29374b', fontSize: '.71rem' }} />
                      {reserve.due_date < localToday() && (
                        <Chip label="Vencida" size="small" color="warning" sx={{ height: 21 }} />
                      )}
                    </Stack>
                    <Typography sx={{ color: '#94a3b8', fontSize: '.77rem', mt: .4 }}>
                      Vence {reserve.due_date} · {accountsById.get(reserve.account_id)?.name || 'Cuenta no disponible'}
                      {reserve.description ? ` · ${reserve.description}` : ''}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: AMBER, fontWeight: 850, fontSize: '1.1rem' }}>
                    {euro(reserve.amount)}
                  </Typography>
                  <Stack direction="row" spacing={.5} flexWrap="wrap" useFlexGap>
                    <Button size="small" variant="outlined" onClick={() => openEdit(reserve)}>
                      Editar
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => setCancelling(reserve)}>
                      Cancelar
                    </Button>
                    <Button size="small" variant="contained" onClick={() => openPayment(reserve)}>
                      Registrar pago
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
          {showHistory && (
            <Stack spacing={.7} mt={1.5}>
              {historical.map((reserve) => (
                <Stack key={reserve.id} direction={{ xs: 'column', sm: 'row' }} spacing={1}
                  justifyContent="space-between" sx={{ p: 1.1, borderRadius: 2, bgcolor: '#19273b' }}>
                  <Box>
                    <Typography sx={{ color: '#cbd5e1', fontWeight: 700 }}>{reserve.name}</Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '.78rem' }}>
                      {reserve.due_date} · {categoryNames[reserve.reserve_type]}
                      {reserve.status === 'paid' ? ' · Pagada' : ' · Cancelada'}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: reserve.status === 'paid' ? GREEN : '#94a3b8', fontWeight: 800 }}>
                    {euro(reserve.amount)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </>
      )}

      <Dialog open={formOpen} fullWidth maxWidth="sm" onClose={() => !save.isPending && setFormOpen(false)}>
        <DialogTitle>{editing ? 'Editar reserva' : 'Nueva reserva profesional'}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: .5, mb: 1.5 }}>
            Introduce el importe que quieras apartar. No se calcula IVA, IRPF ni deducibilidad.
            No dupliques pagos que ya hayas registrado o previsto en otras secciones.
          </Alert>
          <Stack spacing={1.5}>
            <TextField label="Concepto" required value={form.name}
              onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))}
              placeholder="Ej.: IRPF del trimestre" />
            <FormControl fullWidth>
              <InputLabel>Tipo de obligación</InputLabel>
              <Select label="Tipo de obligación" value={form.reserve_type}
                onChange={(e) => setForm((v) => ({ ...v, reserve_type: e.target.value as PVReserveType }))}>
                {(Object.keys(categoryNames) as PVReserveType[]).map((kind) => (
                  <MenuItem key={kind} value={kind}>{categoryNames[kind]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.3}>
              <TextField label="Importe (€)" required fullWidth inputProps={{ inputMode: 'decimal' }}
                value={form.amount} onChange={(e) => setForm((v) => ({ ...v, amount: e.target.value }))} />
              <TextField type="date" label="Fecha de pago prevista" fullWidth
                InputLabelProps={{ shrink: true }} value={form.due_date}
                onChange={(e) => setForm((v) => ({ ...v, due_date: e.target.value }))} />
            </Stack>
            <FormControl fullWidth>
              <InputLabel>Cuenta de la que se apartará dinero</InputLabel>
              <Select label="Cuenta de la que se apartará dinero" value={form.account_id}
                onChange={(e) => setForm((v) => ({ ...v, account_id: e.target.value }))}>
                {activeAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name} · {account.financial_scope === 'personal' ? 'Personal' : 'Profesional'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Notas (opcional)" multiline minRows={2} value={form.description ?? ''}
              onChange={(e) => setForm((v) => ({ ...v, description: e.target.value }))} />
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={save.isPending} onClick={() => setFormOpen(false)}>Cerrar</Button>
          <Button disabled={save.isPending} onClick={saveForm} variant="contained">
            {save.isPending ? 'Guardando...' : 'Guardar reserva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(paying)} fullWidth maxWidth="xs"
        onClose={() => !payment.isPending && setPaying(null)}>
        <DialogTitle>Registrar pago de reserva</DialogTitle>
        {paying && (
          <DialogContent>
            <Typography sx={{ fontWeight: 800 }}>{paying.name}</Typography>
            <Typography sx={{ color: AMBER, fontWeight: 800, fontSize: '1.25rem', mt: .5 }}>
              {euro(paying.amount)}
            </Typography>
            <Typography sx={{ color: '#94a3b8', my: 1 }}>
              Cuenta: {accountsById.get(paying.account_id)?.name || 'Cuenta no disponible'}
            </Typography>
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Esta acción crea un gasto real en la cuenta indicada y liquida la reserva. No la uses si ya has registrado ese pago en Movimientos.
            </Alert>
            <TextField fullWidth type="date" label="Fecha del pago" InputLabelProps={{ shrink: true }}
              value={paymentDate} onChange={(e) => {
                setPaymentDate(e.target.value);
                setRequestId(crypto.randomUUID());
              }} />
            {paymentError && <Alert severity="error" sx={{ mt: 1 }}>{paymentError}</Alert>}
          </DialogContent>
        )}
        <DialogActions>
          <Button disabled={payment.isPending} onClick={() => setPaying(null)}>Volver</Button>
          <Button disabled={payment.isPending || !paymentDate} variant="contained"
            onClick={() => payment.mutate()}>
            {payment.isPending ? 'Registrando...' : 'Confirmar pago'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(cancelling)} maxWidth="xs" fullWidth
        onClose={() => !cancel.isPending && setCancelling(null)}>
        <DialogTitle>Cancelar reserva</DialogTitle>
        {cancelling && (
          <DialogContent>
            <Typography>¿Cancelar «{cancelling.name}» por {euro(cancelling.amount)}?</Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '.84rem', mt: 1 }}>
              Se liberará el importe reservado sin generar ni eliminar movimientos bancarios.
            </Typography>
          </DialogContent>
        )}
        <DialogActions>
          <Button disabled={cancel.isPending} onClick={() => setCancelling(null)}>Volver</Button>
          <Button color="warning" variant="contained" disabled={cancel.isPending}
            onClick={() => cancelling && cancel.mutate(cancelling.id)}>Confirmar cancelación</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
